import 'react-dates/initialize'
import 'react-dates/lib/css/_datepicker.css'

import { datadogRum } from '@datadog/browser-rum'
import { Processing } from '@paywithextend/components'
import { useQueryClient } from '@tanstack/react-query'
import { fetchAuthSession } from 'aws-amplify/auth'
import axios from 'axios'
import { initializeApp } from 'firebase/app'
import {
  fetchAndActivate,
  fetchConfig,
  getRemoteConfig,
} from 'firebase/remote-config'
import _, { debounce } from 'lodash'
import moment from 'moment-timezone'
import Pusher, { Authorizer } from 'pusher-js'
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useCookies } from 'react-cookie'
import { useDispatch, useSelector } from 'react-redux'
import { Route, Switch } from 'react-router'
import { IntercomProvider } from 'react-use-intercom'
import {
  AnalyticsPageName,
  AnalyticsUserTraits,
} from 'src/analytics/analytics.models'
import { analyticsPageNames } from 'src/analytics/AnalyticsContext'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  getAlertStatus,
  getAuthedAlertStatus,
} from 'src/app/AlertBar/redux/alertBar.actions'
import Authed from 'src/app/Authed'
import AuthenticationAgnostic from 'src/app/AuthenticationAgnostic'
import { getNotificationsCount } from 'src/app/Notifications/redux/notifications.actions'
import { selectFetchingInvites } from 'src/app/OrganizationInvites/redux/organizationInvites.selectors'
import { removeOrganizationInvite } from 'src/app/PeopleInvitedToOrg/redux/peopleInvitedToOrg.actions'
import { selectEmailSentFromState } from 'src/app/ResetPassword/redux/resetPassword.selectors'
import {
  createSetIsToastOpen,
  createSetUnhideToast,
} from 'src/app/Toast/redux/toast.actions'
import { selectForceHideToast } from 'src/app/Toast/redux/toast.selectors'
import {
  CreateBulkReceiptAutoMatchFailureToastMessage,
  CreateBulkReceiptAutoMatchPartialToastMessage,
  CreateBulkReceiptAutoMatchSuccessToastMessage,
  CreateBulkVirtualCardAndInvitedUsersSuccessToastMessage,
  CreateBulkVirtualCardFailureToastMessage,
  CreateBulkVirtualCardSuccessToastMessage,
  CreateBulkVirtualCardToastMessage,
} from 'src/app/Toast/Toast'
import Unauthed from 'src/app/Unauthed'
import {
  createCompleteReceiptsAutoMatchBulkUpload,
  getAutoMatchReceiptsEnabled,
  getReceiptsAutoMatchBulkUploadPushStatus,
} from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.actions'
import { ReceiptAutoMatchPushTaskStatus } from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.reducers'
import {
  selectReceiptsAutoMatchIsCreatingExpensesFromState,
  selectReceiptsAutoMatchPushTaskListFromState,
} from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.selectors'
import DowngradedToStarterModal from 'src/component-modal/DowngradedToStarterModal/DowngradedToStarterModal'
import SaasActionRequiredModal, {
  SaasActionRequiredType,
} from 'src/component-modal/SaasActionRequiredModal/SaasActionRequiredModal'
import SvgAlertCircledFilled2 from 'src/components/Svgs/SvgAlertCircledFilled2'
import { Environments } from 'src/configurations'
import { noRedirectURLs } from 'src/constants'
import AstradaContextProvider from 'src/context/AstradaContext/AstradaContext'
import useGlobalModal from 'src/context/GlobalModalContext/useGlobalModal'
import {
  CookieNames,
  CookieValuePaths,
  setCspJwtCookie,
  setCspRefreshTokenCookie,
  setJwtCookie,
  setPerSessionValuesCookie,
  setRefreshTokenCookie,
  setUserSessionTokenIdCookie,
} from 'src/cookies'
import {
  AuthTypes,
  ExtendLocales,
  HidableToastNames,
  OrganizationInviteActions,
  PusherEvents,
  SupportedBrands,
} from 'src/enums'
import { firebaseConfig, firebaseConfigProd } from 'src/firebaseConfigurations'
import {
  appStatisticsKeys,
  useGlobalDebouncedDispatcher,
} from 'src/globalDebouncedDispatcher'
import { useChangedEffect, useCookieState } from 'src/hooks'
import {
  CreditCardListModel,
  PhysicalCardStatus,
} from 'src/models/creditCard.models'
import { RemoteConfigModel } from 'src/models/remoteConfig.models'
import { UserFeature } from 'src/models/user.models'
import {
  JobStatus,
  VirtualCardPushTaskStatus,
} from 'src/models/virtualCardPushTask.models'
import {
  AuthenticatedFlatArchitectureRoutes,
  authenticationAgnosticAppRoute,
} from 'src/navigation/appRoutes'
import { connectedPush } from 'src/navigation/Lnk'
import {
  cspStub,
  expensesTransactionsUrl,
  manageOrgInviteUrlStub,
  onboardingUrl,
  orgInviteDecisionUrl,
} from 'src/navigation/urls'
import { useQueryBrandRedirectSuggestions } from 'src/queries/auth.queries'
import { useQueryOrganizationSaasTier } from 'src/queries/organizations/saas.queries'
import {
  authQueryKeys,
  defaultQueryCacheOptions,
  orgQueryKeys,
} from 'src/queries/queries.constants'
import { queryTransactionDetail } from 'src/queries/transactions.queries'
import { useQueryOwnUser } from 'src/queries/users.queries'
import {
  createResetAppState,
  createSetIsBrandRedirectPopupOpen,
  createSetIsSessionExpiringPopupOpen,
  createSetupGlobalDispatcher,
} from 'src/redux/actions/app.actions'
import {
  createResetMfaCodeStateAction,
  createResetPasswordResetStateAction,
  createSetUnauthedLocaleAction,
  createUpdateAcceptOrgInviteAction,
  createUpdateOrgInviteErrorAction,
  deleteCognitoSignout,
  getCspMe,
  getHeartbeat,
  postAuth0RenewAuth,
  postAuthType,
  postCognitoRenewAuth,
} from 'src/redux/actions/auth.actions'
import { createResetDataState } from 'src/redux/actions/data.actions'
import { getCreditCard } from 'src/redux/actions/data/creditCards.actions'
import { getCreditCards } from 'src/redux/actions/data/creditCards.actions'
import {
  getOrganizationInvite,
  getOrganizationOutOfPocketExpensesEnabled,
  getOrganizationReceiptMgmtEnabled,
  getOrganizations,
  getUserOrganizationInvites,
} from 'src/redux/actions/data/organizations.actions'
import { getOrgIntegrations } from 'src/redux/actions/data/orgIntegrations.actions'
import { getAsyncTransactionUpdateJobStatus } from 'src/redux/actions/data/transactions.actions'
import { getUser } from 'src/redux/actions/data/users.actions'
import { getBulkUploadPushStatus } from 'src/redux/actions/data/virtualCardPushTasks.actions'
import {
  createPostBulkVirtualCardCompletionAction,
  getBulkVirtualCardJobStatus,
  getBulkVirtualCardJobStatusByTaskId,
  getVirtualCard,
} from 'src/redux/actions/data/virtualCards.actions'
import {
  getConfig,
  getFirebaseRemoteConfig,
} from 'src/redux/actions/session.actions'
import {
  selectAllStatisticsLoadedFromState,
  selectIsAttachmentPopupOpenFromState,
  selectIsSessionExpiringPopupOpenFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import {
  selectAcceptOrgInviteFromState,
  selectAuthTypeFromState,
  selectCspJwtFromState,
  selectCspRefreshTokenFromState,
  selectDecodedCspJwt,
  selectDecodedJwt,
  selectEmailFromState,
  selectIsAcceptOrgInvitePopupDismissed,
  selectIsAuthenticatedFromState,
  selectIsCspAuthenticatedFromState,
  selectJwtFromState,
  selectMfaSentFromState,
  selectNullSafeLocaleFromState,
  selectPerSessionValues,
  selectRefreshTokenFromState,
  selectUserIdFromState,
  selectUserListModelFromState,
  selectUserSessionTokenIdFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectAuthedJsonApiRequestConfig } from 'src/redux/selectors/axios.selectors'
import { selectCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import {
  selectOrganizationInvitesModelsFromState,
  selectOrganizationsListModelsFromState,
  selectUserExplicitOrganizationListModelFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { selectOrgIntegrationsModelsFromState } from 'src/redux/selectors/data/orgIntegrations.selectors'
import {
  selectCreditCardsActiveSourceCountStatisticsFromState,
  selectCreditCardsDelegateOwnerMeCountStatisticsFromState,
  selectCreditCardsDelegateUserMeCountStatisticsFromState,
  selectCreditCardsSourceCountStatisticsFromState,
  selectVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsFromState,
  selectVirtualCardsRecipientMeCountStatisticsFromState,
} from 'src/redux/selectors/data/statistics.selectors'
import { selectVirtualCardPushTaskListModelsFromState } from 'src/redux/selectors/data/virtualCardPushTasks.selectors'
import { selectVirtualCardBulkJobModels } from 'src/redux/selectors/data/virtualCards.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectIsLeftFlyoutOpen,
  selectIsPopupOpen,
  selectIsRightFlyoutOpen,
  selectLocationFromState,
  selectQueryParams,
} from 'src/redux/selectors/router.selectors'
import {
  selectBrandName,
  selectEnvConfigFromState,
  selectEnvironmentFromState,
  selectPusherChannelPrefix,
} from 'src/redux/selectors/session.selectors'
import { getFeatureFlagsFromRemoteConfigAction } from 'src/utils/remoteConfig.utils'
import { css } from 'styled-components'

const HEART_BEAT_INTERVAL_MILLISECONDS = 30000
const REFRESH_TOKEN_EXPIRATION_MILLISECONDS = 300000
// Edit SESSION_EXPIRING_MAX_DURATION_MILLISECONDS to test session expiration pop up sooner. 840000 will pop the modal in 1 minute
const SESSION_EXPIRING_MAX_DURATION_MILLISECONDS = 60000
const RENEWAUTH_DEBOUNCE_MILLISECONDS = 10000

export const PusherContext = createContext<Pusher | undefined>(undefined)

const PARTNER_BANKS_LIST = [
  //Current Enum values not accurate. Using this list for checks
  'AMEX',
  'AMEXCEL',
  'BMO-MCIC',
  'BMO_BPC',
  'BOTW',
  'BOTW-MCIC',
  'BOTW_BPC',
  'CNB',
  'COMDATA',
  'COMDATA-GC',
  'HSBC',
  'HSBC-MCIC',
  'HSBC_BPC',
  'JPM',
  'PACWEST',
  'PACWEST_BPC',
  'PNC_VISA_TSYS_6940',
  'PNC_VISA_TSYS_8975',
  'PWB-MCIC',
  'REGIONS',
  'SVB',
  'TCB',
  'TCB_TSYS',
]

const App: React.FC = (): React.ReactElement => {
  const creditCards = useSelector(selectCreditCardListModelsFromState)
  const authedJsonApiRequestConfig = useSelector(
    selectAuthedJsonApiRequestConfig,
  )
  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const envConfig = useSelector(selectEnvConfigFromState)
  const isSessionExpiringPopupOpen = useSelector(
    selectIsSessionExpiringPopupOpenFromState,
  )
  const isAuthenticated = useSelector(selectIsAuthenticatedFromState)
  const jwt = useSelector(selectJwtFromState)
  const decodedJwt = useSelector(selectDecodedJwt)
  const refreshToken = useSelector(selectRefreshTokenFromState)
  const isCspAuthenticated = useSelector(selectIsCspAuthenticatedFromState)
  const cspJwt = useSelector(selectCspJwtFromState)
  const decodedCspJwt = useSelector(selectDecodedCspJwt)
  const cspRefreshToken = useSelector(selectCspRefreshTokenFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const location = useSelector(selectLocationFromState)
  const pusherChannelPrefix = useSelector(selectPusherChannelPrefix)
  const user = useSelector(selectUserListModelFromState)
  const userId = useSelector(selectUserIdFromState)
  const virtualCardPushTasks = useSelector(
    selectVirtualCardPushTaskListModelsFromState,
  )
  const newBulkUploadJobs = useSelector(selectVirtualCardBulkJobModels)
  const userSessionTokenId = useSelector(selectUserSessionTokenIdFromState)
  const isPopupOpen = useSelector(selectIsPopupOpen)
  const isLeftFlyoutOpen = useSelector(selectIsLeftFlyoutOpen)
  const isRightFlyoutOpen = useSelector(selectIsRightFlyoutOpen)
  const isAttachmentPopupOpen = useSelector(
    selectIsAttachmentPopupOpenFromState,
  )
  const organizations = useSelector(selectOrganizationsListModelsFromState)
  const currentOrganization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )
  const currentOrgIntegrations = useSelector(
    selectOrgIntegrationsModelsFromState,
  )
  const authEmail = useSelector(selectEmailFromState)
  const env = useSelector(selectEnvironmentFromState)
  const authType = useSelector(selectAuthTypeFromState)
  const resetPasswordEmailSent = useSelector(selectEmailSentFromState)
  const mfaCodeEmailSent = useSelector(selectMfaSentFromState)
  const forceHideToast = useSelector(selectForceHideToast)
  const orgInvites = useSelector(selectOrganizationInvitesModelsFromState)
  const isAcceptOrgInvitePopupDismissed = useSelector(
    selectIsAcceptOrgInvitePopupDismissed,
  )
  const perSessionValues = useSelector(selectPerSessionValues)
  const fetchingInvites = useSelector(selectFetchingInvites)
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const virtualCardsRecipientMeCountStatistics = useSelector(
    selectVirtualCardsRecipientMeCountStatisticsFromState,
  )
  const creditCardsSourceCountStatistics = useSelector(
    selectCreditCardsSourceCountStatisticsFromState,
  )
  const creditCardsActiveSourceCountStatistics = useSelector(
    selectCreditCardsActiveSourceCountStatisticsFromState,
  )
  const creditCardsDelegateOwnerMeCountStatistics = useSelector(
    selectCreditCardsDelegateOwnerMeCountStatisticsFromState,
  )
  const creditCardsDelegateUserMeCountStatistics = useSelector(
    selectCreditCardsDelegateUserMeCountStatisticsFromState,
  )
  const virtualCardsCardholderOrViewerMeActiveAndInactiveCountStatistics =
    useSelector(
      selectVirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsFromState,
    )
  const receiptsAutoMatchPushTaskList = useSelector(
    selectReceiptsAutoMatchPushTaskListFromState,
  )
  const acceptOrgInvite = useSelector(selectAcceptOrgInviteFromState)
  const brandName = useSelector(selectBrandName)
  const queryParams = useSelector(selectQueryParams)
  const { analytics } = useAnalyticsContext()
  const { show_brand_redirect: showBrandRedirect } = useSelector(
    selectFeatureFlaggerFromState,
  )
  const isCreatingAutomatchExpenses = useSelector(
    selectReceiptsAutoMatchIsCreatingExpensesFromState,
  )
  const { data: orgSaasTier } = useQueryOrganizationSaasTier({
    orgId: currentOrganization?.id,
    enabled: isAuthenticated && currentOrganization?.id !== undefined,
    queryCacheOptions: defaultQueryCacheOptions,
  })

  const globalModal = useGlobalModal()
  const queryClient = useQueryClient()

  const completedReceiptsAutoMatchTasks = receiptsAutoMatchPushTaskList.filter(
    (task) => task.status !== ReceiptAutoMatchPushTaskStatus.PENDING,
  )

  const userHasPendingOrgInvite =
    Object.values(orgInvites)?.length > 0 &&
    !user?.isInExplicitOrg &&
    !isAcceptOrgInvitePopupDismissed

  const bankProperties = useMemo(() => {
    const userCards = Object.values(creditCards) as CreditCardListModel[]

    if (userCards.length === 0) {
      return {
        hasActiveAstradaCard: false,
        partnerBanks: undefined,
        allIssuerBanks: undefined,
      }
    }

    const partnerBanksSet = new Set<string>()
    const allBanksSet = new Set<string>()
    let hasActiveAstradaCard = false

    for (const card of userCards) {
      const bankName = card.issuer
      const isActivePhysicalCard =
        card.physicalCard.status === PhysicalCardStatus.Active

      if (isActivePhysicalCard) {
        hasActiveAstradaCard = true
      }

      if (bankName) {
        allBanksSet.add(bankName)

        if (!hasActiveAstradaCard && PARTNER_BANKS_LIST.includes(bankName)) {
          partnerBanksSet.add(bankName)
        }
      }
    }

    return {
      hasActiveAstradaCard,
      partnerBanks:
        !hasActiveAstradaCard && partnerBanksSet.size > 0
          ? Array.from(partnerBanksSet).join(', ')
          : undefined,
      allIssuerBanks:
        hasActiveAstradaCard && allBanksSet.size > 0
          ? Array.from(allBanksSet).join(', ')
          : undefined,
    }
  }, [creditCards])

  const [cookies] = useCookies([CookieNames.FeatureFlagger])

  useEffect((): void => {
    // keeping the firebase config code here because we can't call it from the server side
    // having firebase config in App.tsx allows it to trigger as soon as a user opens the app rather than waiting for signin
    const app = initializeApp(
      env === Environments.Prod ? firebaseConfigProd : firebaseConfig,
    )
    const remoteConfig = getRemoteConfig(app)
    remoteConfig.settings.minimumFetchIntervalMillis =
      env === Environments.Prod ? 900000 : 30

    fetchAndActivate(remoteConfig).then(
      (response: unknown) => {
        if (response) {
          dispatch(
            getFeatureFlagsFromRemoteConfigAction(
              remoteConfig,
              cookies.feature_flagger,
              env,
            ),
          )
        } else {
          fetchConfig(remoteConfig).then(
            () => {
              dispatch(
                getFeatureFlagsFromRemoteConfigAction(
                  remoteConfig,
                  cookies.feature_flagger,
                  env,
                ),
              )
            },
            (err: unknown) => {
              dispatch(
                getFirebaseRemoteConfig(
                  RemoteConfigModel.defaultConfig(),
                  cookies.feature_flagger,
                ),
              )
              /* eslint-disable-next-line no-console */
              console.log(err)
              return Promise.reject(err)
            },
          )
        }
      },
      (err: unknown) => {
        dispatch(
          getFirebaseRemoteConfig(
            RemoteConfigModel.defaultConfig(),
            cookies.feature_flagger,
          ),
        )
        /* eslint-disable-next-line no-console */
        console.log(err)
        return Promise.reject(err)
      },
    )
  }, [userId])

  const email = user?.email || authEmail

  const dispatch = useDispatch()

  useEffect((): void => {
    if (isAuthenticated && currentOrganization !== undefined) {
      dispatch(getCreditCards({}))
    }
  }, [isAuthenticated, currentOrganization?.id])

  // scroll
  useEffect((): void => {
    window.scrollTo(0, 0)
  }, [calculatedBackgroundLocation])

  // modal open scroll prevention
  useEffect(() => {
    const isModalOpen =
      isPopupOpen ||
      isLeftFlyoutOpen ||
      isRightFlyoutOpen ||
      isAttachmentPopupOpen
    if (isModalOpen) {
      document.body.classList.add('modal-open')
    } else {
      document.body.classList.remove('modal-open')
    }
  }, [
    isPopupOpen,
    isLeftFlyoutOpen,
    isRightFlyoutOpen,
    isAttachmentPopupOpen,
    isSessionExpiringPopupOpen,
  ])

  // pusher
  const [pusher, setPusher] = useState<Pusher | undefined>()
  useEffect((): void => {
    if (pusher !== undefined) {
      pusher.disconnect()
    }

    const newPusher =
      envConfig.PUSHER_KEY !== undefined &&
      envConfig.PUSHER_CLUSTER !== undefined &&
      userId !== undefined
        ? new Pusher(envConfig.PUSHER_KEY, {
            authorizer: (): Authorizer => ({
              authorize: (socketId, callback): void => {
                axios
                  .post(
                    '/pusherauth',
                    {
                      socket_id: socketId,
                      channel_name: `${pusherChannelPrefix}${userId}`,
                    },
                    authedJsonApiRequestConfig,
                  )
                  .then((response) => callback(null, response.data))
                  .catch((error) =>
                    callback(
                      {
                        name: 'Error',
                        message: error.response,
                      },
                      error.response,
                    ),
                  )
              },
            }),
            cluster: envConfig.PUSHER_CLUSTER,
          })
        : undefined

    if (newPusher !== undefined) {
      const channel = newPusher.subscribe(`${pusherChannelPrefix}${userId}`)

      channel.bind(
        PusherEvents.BulkReceiptCapture,
        (data: { id: string; action: string }) => {
          dispatch(getReceiptsAutoMatchBulkUploadPushStatus(data.id))
        },
      )

      channel.bind(
        PusherEvents.BulkUpload,
        (data: { id: string; action: string }) => {
          dispatch(getBulkUploadPushStatus(data.id))
        },
      )

      channel.bind(
        PusherEvents.CreditCard,
        (data: { id: string; action: string }) => {
          dispatch(getCreditCard(data.id))
          dispatchStatistics(
            [
              appStatisticsKeys.CreditCardsDelegateCountStatistics,
              appStatisticsKeys.CreditCardsActiveSourceCountStatistics,
              appStatisticsKeys.CreditCardsSourceCountStatistics,
              appStatisticsKeys.CreditCardsDelegateUserMeCountStatistics,
              appStatisticsKeys.CreditCardsActiveDelegateCountStatistics,
            ],
            moment().format('dddd, Do MMM YYYY, h:mm:ss A'),
          )
          queryClient.invalidateQueries({
            queryKey: authQueryKeys.features([
              UserFeature.VIEW_VIRTUAL_CARD_FEATURES,
            ]),
          })
        },
      )

      channel.bind(
        PusherEvents.Transaction,
        async (data: { id: string; action: string }) => {
          // TODO: React Query, this will not provide live updates for users viewing VCN detail pages that they are the recipient of
          // check if user is currently viewing the Expenses Transactions Page
          if (location.pathname.includes(expensesTransactionsUrl())) {
            queryTransactionDetail(
              queryClient,
              authedJsonApiRequestConfig,
              data.id,
            )
          }
        },
      )

      channel.bind(
        PusherEvents.AsyncJob,
        (data: { id: string; action: string }) => {
          dispatch(getAsyncTransactionUpdateJobStatus(data.id))
        },
      )

      channel.bind(
        PusherEvents.User,
        (data: { id: string; action: string }) => {
          dispatch(getUser(data.id))
        },
      )

      channel.bind(
        PusherEvents.VirtualCard,
        (data: { id: string; action: string }) => {
          dispatch(getVirtualCard(data.id))
        },
      )

      channel.bind(
        PusherEvents.OrganizationInvite,
        (data: { id: string; action: string }) => {
          switch (data.action) {
            case OrganizationInviteActions.CREATED: {
              dispatch(getOrganizationInvite(data.id))
              break
            }
            case OrganizationInviteActions.UPDATED: {
              dispatch(removeOrganizationInvite(data.id))
              break
            }
          }
        },
      )

      channel.bind(
        PusherEvents.BulkVirtualCardAction,
        (data: { id: string; action: string }) => {
          dispatch(getBulkVirtualCardJobStatus(data.id))
        },
      )

      channel.bind(
        PusherEvents.BulkVirtualCardTask,
        (data: { id: string; action: string }) => {
          dispatch(getBulkVirtualCardJobStatusByTaskId(data.id))
        },
      )

      channel.bind(
        PusherEvents.SaasOrganization,
        async (data: { id: string; action: string }) => {
          // Invalidate SaaS tier and subscription queries to refresh the data
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: orgQueryKeys.saasTier(data.id),
            }),
            queryClient.invalidateQueries({
              queryKey: orgQueryKeys.organizationSubscription(data.id),
            }),
            queryClient.invalidateQueries({
              queryKey: orgQueryKeys.organizationUsages(data.id),
            }),
          ])
        },
      )
    }

    setPusher(newPusher)
  }, [envConfig, userId, authedJsonApiRequestConfig])

  // jwt and refresh token cookies
  useEffect((): void => {
    setJwtCookie(envConfig, jwt)
    setRefreshTokenCookie(envConfig, refreshToken)
    setCspJwtCookie(envConfig, cspJwt)
    setCspRefreshTokenCookie(envConfig, cspRefreshToken)
    setUserSessionTokenIdCookie(envConfig, userSessionTokenId)
  }, [jwt, refreshToken, cspJwt, cspRefreshToken, userSessionTokenId])

  // update per-session cookie when per-session values change
  useEffect((): void => {
    if (perSessionValues !== undefined)
      setPerSessionValuesCookie(perSessionValues)
  }, [perSessionValues])

  // receipts auto-match push tasks toasts
  useEffect(() => {
    if (
      completedReceiptsAutoMatchTasks.length ===
        receiptsAutoMatchPushTaskList.length &&
      completedReceiptsAutoMatchTasks.length > 0
    ) {
      dispatch(createSetIsToastOpen(false))
      dispatch(getNotificationsCount())
      const successTasks = receiptsAutoMatchPushTaskList.filter(
        (task) => task.status === ReceiptAutoMatchPushTaskStatus.SUCCESS,
      )
      if (successTasks.length === receiptsAutoMatchPushTaskList.length) {
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: (
              <CreateBulkReceiptAutoMatchSuccessToastMessage
                isCreatingExpenses={isCreatingAutomatchExpenses}
              />
            ),
          }),
        )
      } else if (successTasks.length > 0) {
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: (
              <CreateBulkReceiptAutoMatchPartialToastMessage
                successful={successTasks.length}
                failed={
                  receiptsAutoMatchPushTaskList.length - successTasks.length
                }
                isCreatingExpenses={isCreatingAutomatchExpenses}
              />
            ),
            image: (
              <SvgAlertCircledFilled2
                css={css`
                  transform: scale(0.8);
                  margin-left: -10px;
                `}
              />
            ),
            partialSuccessColorScheme: true,
          }),
        )
      } else if (successTasks.length === 0) {
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: (
              <CreateBulkReceiptAutoMatchFailureToastMessage
                isCreatingExpenses={isCreatingAutomatchExpenses}
              />
            ),
            hideImage: true,
            failureColorScheme: true,
          }),
        )
      }

      dispatch(createCompleteReceiptsAutoMatchBulkUpload())
    }
  }, [completedReceiptsAutoMatchTasks.length])

  // virtual card push tasks toast
  useEffect((): void => {
    const bulkUploadJobsArray = Object.values(newBulkUploadJobs).filter(
      (job) => job.status === JobStatus.Running,
    )

    bulkUploadJobsArray.map((job) => {
      const pushTasksArray = Object.values(virtualCardPushTasks).filter(
        (task) => task.uploadId === job.id,
      )
      const failedTasks = pushTasksArray.filter(
        (task) => task.status === VirtualCardPushTaskStatus.Failed,
      )
      const completedTasks = pushTasksArray.filter(
        (task) => task.status === VirtualCardPushTaskStatus.Completed,
      )

      if (pushTasksArray.length > 0) {
        if (!forceHideToast) {
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <CreateBulkVirtualCardToastMessage
                  displayName={job?.displayName || ''}
                  virtualCardPushTasks={pushTasksArray}
                />
              ),
              hidableToastName: HidableToastNames.BulkVirtualCardAction,
              inProgressColorScheme: true,
              image: (
                <Processing
                  css={css`
                    transform: scale(1.2);
                  `}
                />
              ),
              bulkToast: true,
            }),
          )
        }
        if (completedTasks.length === pushTasksArray.length) {
          dispatch(
            createSetUnhideToast(HidableToastNames.BulkVirtualCardAction),
          )
          dispatch(createPostBulkVirtualCardCompletionAction(job.id))
          if (job.invitingUsers) {
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: (
                  <CreateBulkVirtualCardAndInvitedUsersSuccessToastMessage
                    displayName={job?.displayName || ''}
                    virtualCardPushTasks={pushTasksArray}
                  />
                ),
              }),
            )
          } else {
            dispatch(
              createSetIsToastOpen(true, {
                toastMessage: (
                  <CreateBulkVirtualCardSuccessToastMessage
                    displayName={job?.displayName || ''}
                    virtualCardPushTasks={pushTasksArray}
                  />
                ),
              }),
            )
          }
        }
        if (failedTasks.length > 0) {
          dispatch(
            createSetUnhideToast(HidableToastNames.BulkVirtualCardAction),
          )
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <CreateBulkVirtualCardFailureToastMessage
                  displayName={job?.displayName || ''}
                />
              ),
              failureColorScheme: true,
              hideImage: true,
            }),
          )
        }
      }
    })
  }, [virtualCardPushTasks, newBulkUploadJobs, forceHideToast])

  // triggers for becoming authenticated/unauthenticated
  useChangedEffect((): void => {
    try {
      window.localStorage.setItem('authChangedTrigger', 'true')
    } catch {
      return
    }
  }, [jwt, refreshToken, cspJwt, cspRefreshToken])
  const handleLocalStorage = (): void => {
    try {
      const authChangedTrigger =
        window.localStorage.getItem('authChangedTrigger')
      window.localStorage.removeItem('authChangedTrigger')
      if (authChangedTrigger && !document.hasFocus()) {
        window.location.reload()
      }
    } catch {
      return
    }
  }
  useEffect(() => {
    window.addEventListener('storage', handleLocalStorage) // triggers only on NON active tabs
    return (): void => {
      window.removeEventListener('storage', handleLocalStorage)
    }
  }, [])

  // locale
  useEffect(() => {
    if (locale !== undefined) {
      moment.locale(locale)
      document.documentElement.setAttribute('lang', locale.toLowerCase())
    }
  }, [locale])

  // axios interceptor
  const responseInterceptor = useRef<number | undefined>()
  useEffect((): (() => void) => {
    responseInterceptor.current = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // FORBIDDEN / NOT AUTHENTICATED
        if (error.response?.status === 401) {
          if (authType === AuthTypes.auth0) {
            if (isAuthenticated) {
              dispatch(getHeartbeat())
            }
            if (isCspAuthenticated) {
              dispatch(getHeartbeat(true))
            }
          } else {
            cognitoSessionValid()
          }
          // UPGRADE/PAYMENT REQUIRED
        } else if (error.response?.status === 402) {
          if (
            error.response.data.exceptionType ===
              SaasActionRequiredType.DOWNGRADE_ACTION_REQUIRED &&
            user?.hasOrgAdminPermissions
          ) {
            globalModal.showGlobalModal(
              <DowngradedToStarterModal
                isOpen
                onClose={() => globalModal.closeGlobalModal()}
                {...error.response.data}
              />,
            )
          } else {
            globalModal.showGlobalModal(
              <SaasActionRequiredModal
                isOpen
                onClose={() => globalModal.closeGlobalModal()}
                {...error.response.data}
              />,
            )
          }
        }
        return Promise.reject(error)
      },
    )
    return (): void => {
      if (responseInterceptor.current !== undefined) {
        axios.interceptors.response.eject(responseInterceptor.current)
        responseInterceptor.current = undefined
      }
    }
  }, [isAuthenticated, isCspAuthenticated, user])

  // determine if cognito session is valid (only applies to Cognito auth)
  const cognitoSessionValid = useCallback(async () => {
    // This function only handles Cognito authentication - Auth0 has its own session handling
    if (authType !== AuthTypes.cognito) {
      return
    }
    let hasAmplifySession = false
    try {
      const session = await fetchAuthSession()
      hasAmplifySession = !!session.tokens
    } catch {
      hasAmplifySession = false
    }

    if (hasAmplifySession) {
      return
    }

    const activeJwt = isCspAuthenticated ? cspJwt : jwt
    const activeDecodedJwt = isCspAuthenticated ? decodedCspJwt : decodedJwt
    const isJwtStillValid =
      activeJwt !== undefined &&
      activeDecodedJwt !== undefined &&
      moment(new Date(activeDecodedJwt.exp * 1000)).isAfter(moment())

    if (!isJwtStillValid) {
      dispatch(deleteCognitoSignout(true, analytics, isCspAuthenticated))
    }
  }, [
    analytics,
    authType,
    cspJwt,
    decodedCspJwt,
    decodedJwt,
    dispatch,
    isCspAuthenticated,
    jwt,
  ])

  useEffect(() => {
    if (isAuthenticated || isCspAuthenticated) {
      cognitoSessionValid()
    }
  }, [cognitoSessionValid, isAuthenticated, isCspAuthenticated, authType])

  // heartbeat
  const heartbeatInterval = useRef<NodeJS.Timeout | undefined>()
  useEffect(() => {
    if (isCspAuthenticated && decodedCspJwt !== undefined) {
      heartbeatInterval.current = setInterval((): void => {
        const msUntilCspJwtExpiration = moment(
          new Date(decodedCspJwt.exp * 1000),
        ).diff(moment())
        if (
          msUntilCspJwtExpiration < SESSION_EXPIRING_MAX_DURATION_MILLISECONDS
        ) {
          dispatch(createSetIsSessionExpiringPopupOpen(true))
        }
      }, HEART_BEAT_INTERVAL_MILLISECONDS)
    } else if (isAuthenticated && decodedJwt !== undefined) {
      heartbeatInterval.current = setInterval((): void => {
        const msUntilJwtExpiration = moment(
          new Date(decodedJwt.exp * 1000),
        ).diff(moment())

        if (msUntilJwtExpiration < SESSION_EXPIRING_MAX_DURATION_MILLISECONDS) {
          dispatch(createSetIsSessionExpiringPopupOpen(true))
        }
      }, HEART_BEAT_INTERVAL_MILLISECONDS)
    }
    return (): void => {
      if (heartbeatInterval.current !== undefined) {
        clearInterval(heartbeatInterval.current)
      }
    }
  }, [isCspAuthenticated, isAuthenticated, decodedJwt, decodedCspJwt])

  // renewauth
  const handleAuthenticatedActions = useCallback(
    debounce((): void => {
      if (
        decodedCspJwt !== undefined &&
        cspRefreshToken !== undefined &&
        !isSessionExpiringPopupOpen
      ) {
        const msUntilCspJwtExpiration = moment(
          new Date(decodedCspJwt.exp * 1000),
        ).diff(moment())
        if (msUntilCspJwtExpiration < REFRESH_TOKEN_EXPIRATION_MILLISECONDS) {
          if (authType === AuthTypes.cognito) {
            dispatch(postCognitoRenewAuth(false, true))
          } else {
            // TODO: investigate this further. Was getting 403s when working on this pr (https://github.com/paywithextend/extend-app/pull/1884). Wrong refresh token being used?
            dispatch(postAuth0RenewAuth(email, true))
          }
        }
      } else if (
        decodedJwt !== undefined &&
        refreshToken !== undefined &&
        !isSessionExpiringPopupOpen
      ) {
        const msUntilJwtExpiration = moment(
          new Date(decodedJwt.exp * 1000),
        ).diff(moment())
        if (msUntilJwtExpiration < REFRESH_TOKEN_EXPIRATION_MILLISECONDS) {
          if (authType === AuthTypes.cognito) {
            dispatch(postCognitoRenewAuth())
          } else {
            dispatch(postAuth0RenewAuth(email))
          }
        }
      }
    }, RENEWAUTH_DEBOUNCE_MILLISECONDS),
    [
      decodedJwt,
      refreshToken,
      decodedCspJwt,
      cspRefreshToken,
      isSessionExpiringPopupOpen,
      authType,
    ],
  )

  useEffect(() => {
    window.addEventListener('mousedown', handleAuthenticatedActions)
    window.addEventListener('keydown', handleAuthenticatedActions)
    return (): void => {
      window.removeEventListener('mousedown', handleAuthenticatedActions)
      window.removeEventListener('keydown', handleAuthenticatedActions)
    }
  }, [handleAuthenticatedActions])

  // first login
  const {
    // cookieValue: firstLogin,
    updateCookieValue: setFirstLogin,
  } = useCookieState(CookieNames.Authed, CookieValuePaths.firstLogin, true)

  // first login
  const {
    cookieValue: unauthedLocaleCookie,
    updateCookieValue: setUnauthedLocaleCookie,
  } = useCookieState(
    CookieNames.Unauthed,
    CookieValuePaths.unauthedLocale,
    ExtendLocales.enUS,
  )

  // set unauthed locale on app load
  useEffect((): void => {
    if (
      queryParams.locale !== undefined &&
      Object.values(ExtendLocales).includes(queryParams.locale)
    ) {
      dispatch(createSetUnauthedLocaleAction(queryParams.locale))
      setUnauthedLocaleCookie(queryParams.locale)
    } else {
      dispatch(createSetUnauthedLocaleAction(unauthedLocaleCookie))
    }
  }, [])

  // set unauthed locale user's locale to persist user locale into unauthed view (easier to do this here rather than every log out logic point)
  useEffect((): void => {
    if (isAuthenticated && user && queryParams.locale === undefined) {
      dispatch(createSetUnauthedLocaleAction(locale))
      setUnauthedLocaleCookie(locale) // set cookie too
    }
  }, [isAuthenticated, user])

  const [isFirstAuthedRender, setIsFirstAuthedRender] = useState<boolean>(true)

  // setup global debounced dispatcher
  const globalDispatcher = useGlobalDebouncedDispatcher()

  // check for brand suggestions in case  we want to redirect the user to log into a different brand
  const { data: brandRedirectSuggestions } = useQueryBrandRedirectSuggestions({
    enabled:
      showBrandRedirect && !perSessionValues?.isBrandSuggestionPopupDismissed,
  })

  // potentially open brand redirect popup
  useEffect(() => {
    if (brandRedirectSuggestions) {
      dispatch(
        createSetIsBrandRedirectPopupOpen(brandRedirectSuggestions.length > 0),
      )
    }
  }, [brandRedirectSuggestions])

  useEffect(() => {
    if (isAuthenticated) {
      if (isFirstAuthedRender) {
        // only run these setup calls on first render to avoid redoing the setup on every App re-render
        setIsFirstAuthedRender(false)
        // save initialized global debounced dispatcher to state
        dispatch(createSetupGlobalDispatcher(globalDispatcher))
      }
    } else {
      setIsFirstAuthedRender(true)
    }
  }, [isFirstAuthedRender, isAuthenticated])

  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  useEffect((): void => {
    if (isAuthenticated) {
      dispatchStatistics([
        appStatisticsKeys.VirtualCardsRecipientMeCountStatistics,
        appStatisticsKeys.CreditCardsSourceCountStatistics,
        appStatisticsKeys.CreditCardsDelegateOwnerMeCountStatistics,
        appStatisticsKeys.CreditCardsDelegateUserMeCountStatistics,
        appStatisticsKeys.VirtualCardsCardholderOrViewerMeActiveAndInactiveCountStatistics,
        appStatisticsKeys.VirtualCardsRecipientMeCountStatistics,
        appStatisticsKeys.CreditCardsDelegateCountStatistics,
      ])
    }
  }, [isAuthenticated, dispatchStatistics])

  useEffect((): void => {
    if (isAuthenticated) {
      setFirstLogin(false)
      dispatch(postAuthType({ data: email }))
    }
  }, [isAuthenticated])

  useEffect((): void => {
    if (isAuthenticated && currentOrganization !== undefined) {
      dispatch(getOrganizationReceiptMgmtEnabled(currentOrganization.id))
      dispatch(
        getOrganizationOutOfPocketExpensesEnabled(currentOrganization.id),
      )
      dispatch(getAutoMatchReceiptsEnabled(currentOrganization.id))
    }
  }, [isAuthenticated, currentOrganization?.id])

  useEffect((): void => {
    if (isAuthenticated && userId) {
      dispatch(getNotificationsCount())
    }
  }, [isAuthenticated, userId])

  // config
  useEffect((): void => {
    dispatch(getConfig())
  }, [locale])

  // reset data and app state when not authed
  useChangedEffect((): void => {
    if (!isAuthenticated && !isCspAuthenticated) {
      dispatch(createResetDataState())
      dispatch(createResetAppState())
    }
  }, [isAuthenticated, isCspAuthenticated])

  useEffect(() => {
    isAuthenticated
      ? dispatch(getAuthedAlertStatus())
      : dispatch(getAlertStatus())
  }, [isAuthenticated, isCspAuthenticated])

  // organizations
  useEffect((): void => {
    if (isAuthenticated && Object.keys(organizations).length === 0) {
      dispatch(getOrganizations())
    }
  }, [isAuthenticated, organizations])

  // organization integrations
  useEffect((): void => {
    if (
      isAuthenticated &&
      currentOrganization !== undefined &&
      !currentOrgIntegrations
    ) {
      dispatch(getOrgIntegrations(currentOrganization.id))
    }
  }, [isAuthenticated, currentOrganization, user])

  // get users organization invites
  useEffect((): void => {
    if (isAuthenticated) {
      dispatch(
        getUserOrganizationInvites({
          sortDirection: 'ASC',
          sortField: 'createdAt',
        }),
      )
    }
  }, [isAuthenticated])

  // route to onboarding name flow if user is logged in, the user object is in the redux store, and they are not part of an org
  useEffect((): void => {
    if (
      isAuthenticated &&
      user &&
      !user?.isInExplicitOrg &&
      !location.pathname.includes(manageOrgInviteUrlStub()) &&
      !location.pathname.includes(cspStub()) &&
      !noRedirectURLs?.includes(location.pathname) &&
      calculatedBackgroundLocation.pathname !== orgInviteDecisionUrl() &&
      !fetchingInvites
    ) {
      if (userHasPendingOrgInvite) {
        if (
          location.pathname !== orgInviteDecisionUrl() &&
          calculatedBackgroundLocation.pathname !== orgInviteDecisionUrl() &&
          !acceptOrgInvite
        ) {
          dispatch(connectedPush(orgInviteDecisionUrl()))
        }
      } else if (location.pathname !== onboardingUrl()) {
        dispatch(connectedPush(onboardingUrl()))
      }
    }
  }, [
    isAuthenticated,
    user,
    location,
    userHasPendingOrgInvite,
    orgInvites,
    fetchingInvites,
  ])

  useQueryOwnUser({ enabled: isAuthenticated && !isCspAuthenticated })

  useEffect(() => {
    if (isAuthenticated && Object.values(orgInvites)?.length === 0) {
      if (acceptOrgInvite === undefined) {
        if (isCspAuthenticated) {
          dispatch(getCspMe())
        }

        dispatch(createUpdateAcceptOrgInviteAction(false))
      } else if (acceptOrgInvite) {
        dispatch(createUpdateOrgInviteErrorAction(true))
      }
    }
  }, [isAuthenticated, acceptOrgInvite, orgInvites])

  // user traits
  useEffect((): void => {
    if (!isAuthenticated || userId === undefined) return

    const areRequiredStatsLoaded =
      statisticsLoaded.creditCardsSourceCountStatisticsLoaded &&
      statisticsLoaded.creditCardsActiveSourceCountStatisticsLoaded &&
      statisticsLoaded.creditCardsDelegateOwnerMeCountStatisticsLoaded &&
      statisticsLoaded.creditCardsDelegateUserMeCountStatisticsLoaded &&
      statisticsLoaded.virtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsLoaded &&
      statisticsLoaded.virtualCardsRecipientMeCountStatisticsLoaded

    // Identify the user with just these readily available props as soon as possible to properly track
    // page views and other events that happen before the user object and stats are loaded
    let userTraits: AnalyticsUserTraits = {
      brand_name: brandName,
      user_id: userId,
      email: email,
      is_internal: /^.*@paywithextend\.com$/.test(email),
      ...(brandName === SupportedBrands.Extend
        ? { subscription_plan_type: orgSaasTier?.tier }
        : { subscription_plan_type: 'N/A' }),
    }

    if (
      user !== undefined &&
      currentOrganization !== undefined &&
      areRequiredStatsLoaded
    ) {
      // Wait for all required data to be loaded before identifying the user with remaining traits
      // Otherwise, we'll be sending multiple identify calls with incomplete snapshots of the user

      const { partnerBanks, allIssuerBanks, hasActiveAstradaCard } =
        bankProperties

      userTraits = {
        ...userTraits,
        created_at: moment(user.createdAt).toISOString(),
        locale_code: user.locale,
        full_name: user.fullName,
        verified: user.verified,
        org_id: user.organization?.id,
        org_name: currentOrganization?.name,
        org_role: user.organization?.role,
        has_accounts: creditCardsSourceCountStatistics > 0,
        has_active_accounts: creditCardsActiveSourceCountStatistics > 0,
        has_created_budgets: creditCardsDelegateOwnerMeCountStatistics > 0,
        has_budgets: creditCardsDelegateUserMeCountStatistics > 0,
        has_created_virtual_cards:
          virtualCardsCardholderOrViewerMeActiveAndInactiveCountStatistics > 0,
        has_virtual_cards: virtualCardsRecipientMeCountStatistics > 0,
        has_active_astrada_card: hasActiveAstradaCard,
        issuer_banks: allIssuerBanks,
        partner_bank: partnerBanks,
      }
    }
    // Only identify the user if identify hasn't been called for this user in this session
    // or if the user traits have changed since the last identify call
    const lastTraits = analytics?.lastIdentifiedUserTraits
    if (!_.isEqual(lastTraits, userTraits)) {
      analytics?.identify(userId, userTraits)
    }
  }, [
    isAuthenticated,
    userId,
    user,
    currentOrganization,
    orgSaasTier,
    brandName,
    bankProperties,
    statisticsLoaded.creditCardsSourceCountStatisticsLoaded,
    statisticsLoaded.creditCardsActiveSourceCountStatisticsLoaded,
    statisticsLoaded.creditCardsDelegateOwnerMeCountStatisticsLoaded,
    statisticsLoaded.creditCardsDelegateUserMeCountStatisticsLoaded,
    statisticsLoaded.virtualCardsCardholderOrViewerMeActiveAndInactiveCountStatisticsLoaded,
    statisticsLoaded.virtualCardsRecipientMeCountStatisticsLoaded,
  ])

  // analytics
  useEffect((): void => {
    // replace actual IDs in the path with ':id'
    const genericPath: string = location.pathname.replace(
      /vc_([\w]+)|cc_([\w]+)|bur_([\w]+)|u_([\w]+)|txn_([\w]+)|org_([\w]+)|org_ivt_([\w]+)|org_erc_([\w]+)|org_er_([\w]+)|ec_([\w]+)|dept_([\w]+)|conn_([\w]+)/g,
      ':id',
    )
    const pageName: AnalyticsPageName = analyticsPageNames[genericPath]
    if (pageName !== undefined) {
      analytics?.page(pageName)
    } else {
      // Ideally, we shouldn't be flooding the analytics with "unknown" pages
      // Once all pages that Product wants to track are mapped, this should be removed
      analytics?.pageLegacy(genericPath)
    }
  }, [location])

  // close toast on successful auth
  useEffect((): void => {
    if (isAuthenticated) {
      dispatch(createSetIsToastOpen(false))
    }
  }, [isAuthenticated])

  const [resetPasswordCountdown, setResetPasswordCountdown] = useState(0)

  // start countdown for reset password email being sent
  useEffect((): void => {
    if (resetPasswordEmailSent) {
      setResetPasswordCountdown(60)
    }
  }, [resetPasswordEmailSent])

  // reset password countdown
  useEffect(() => {
    resetPasswordCountdown > 0
      ? setTimeout(
          () => setResetPasswordCountdown(resetPasswordCountdown - 1),
          1000,
        )
      : dispatch(createResetPasswordResetStateAction())
  }, [resetPasswordCountdown])

  const [mfaCodeCountdown, setMfaCodeCountdown] = useState(0)

  // start countdown for mfa code email being sent
  useEffect((): void => {
    if (mfaCodeEmailSent) {
      setMfaCodeCountdown(60 * 10) // mfa code is valid for 10 minutes
    }
  }, [mfaCodeEmailSent])

  // mfa code countdown
  useEffect(() => {
    mfaCodeCountdown > 0
      ? setTimeout(() => setMfaCodeCountdown(mfaCodeCountdown - 1), 1000)
      : dispatch(createResetMfaCodeStateAction())
  }, [mfaCodeCountdown])

  // datadog tracking
  useEffect(() => {
    datadogRum.startSessionReplayRecording()

    if (user) {
      datadogRum.setUser({
        id: user.id,
        orgId: user.organizationId,
      })
    } else {
      datadogRum.clearUser()
    }
  }, [user])

  // @TODO: ARCHITECTURE
  // Any new page/route we create to add into the new architecture should have their pathname
  // included into this array
  const inFlatArchitectureBuild = AuthenticatedFlatArchitectureRoutes.find(
    (item) => {
      return item.path === calculatedBackgroundLocation.pathname
    },
  )

  return (
    <Switch location={calculatedBackgroundLocation}>
      <Route {...authenticationAgnosticAppRoute.routeProps}>
        <AuthenticationAgnostic />
      </Route>
      {isAuthenticated && (
        <PusherContext.Provider value={pusher}>
          <AstradaContextProvider>
            {/* TODO: remove hardcoded appId */}
            <IntercomProvider
              appId="cq3taadk"
              apiBase="https://api-iam.intercom.io"
            >
              {/*
                Conditionally check for if the page is part of the new flat page architecture
              */}
              {inFlatArchitectureBuild ? (
                <Switch>
                  {AuthenticatedFlatArchitectureRoutes.map(
                    ({ path, Component }, index) => {
                      return (
                        <Route exact path={path} key={index}>
                          <Component />
                        </Route>
                      )
                    },
                  )}
                </Switch>
              ) : (
                // LEGACY AUTHENTICATED WRAPPER
                <Authed />
              )}
            </IntercomProvider>
          </AstradaContextProvider>
        </PusherContext.Provider>
      )}
      {!isAuthenticated && <Unauthed />}
    </Switch>
  )
}

export default App
